/** Menu: a popover of actions, radio choices or checkboxes under a trigger. Opens with dd-in, closes with dd-out. */
import type { ComponentProps } from "react";
import { DropdownMenu as Primitive } from "radix-ui";
import { cx } from "./cx";

export const Menu = Primitive.Root;
export const MenuGroup = Primitive.Group;
export const MenuRadioGroup = Primitive.RadioGroup;

/** The trigger is always the child you pass (a kit Button). */
export function MenuTrigger(props: ComponentProps<typeof Primitive.Trigger>) {
  return <Primitive.Trigger asChild {...props} />;
}

/**
 * A long menu (110 State House districts, the counties) scrolls inside the room the viewport leaves it, 8 px clear of
 * its edge, never past it where nothing can reach it (#341, 28 Oct): wheel, trackpad and touch scroll it, and the arrow
 * keys bring the highlighted row into view. It opens on its ticked row. A menu that should stay shorter sets
 * `[--menu-cap:20rem]`, never a height of its own (that would beat the viewport's limit).
 */
export function MenuContent({ className, sideOffset = 6, align = "end", collisionPadding = 8, ...props }: ComponentProps<typeof Primitive.Content>) {
  return (
    <Primitive.Portal>
      <Primitive.Content
        ref={showChosen}
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        align={align}
        collisionPadding={collisionPadding}
        className={cx(
          "z-50 max-h-[min(var(--menu-cap,100dvh),var(--radix-dropdown-menu-content-available-height))] min-w-44 origin-(--radix-dropdown-menu-content-transform-origin) overflow-y-auto overflow-x-hidden overscroll-contain rounded-xl bg-popover p-1 text-popover-foreground shadow-popover data-[state=open]:animate-menu-in data-[state=closed]:animate-menu-out",
          className,
        )}
        {...props}
      />
    </Primitive.Portal>
  );
}

/** On opening, bring the ticked row into view; opened from the keyboard, the arrows go on from it. */
function showChosen(menu: HTMLDivElement | null) {
  if (!menu) return;
  requestAnimationFrame(() => {
    const chosen = menu.querySelector<HTMLElement>('[aria-checked="true"]');
    if (!chosen) return;
    chosen.scrollIntoView?.({ block: "nearest" });
    const active = document.activeElement;
    if (active !== menu && active instanceof HTMLElement && menu.contains(active)) chosen.focus({ preventScroll: true });
  });
}

const ITEM =
  "relative flex min-h-9 cursor-default select-none items-center gap-2 rounded-lg px-2.5 py-2 text-body leading-none text-secondary-foreground outline-none transition-colors data-[highlighted]:bg-accent data-[highlighted]:text-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:shrink-0";

const INDICATOR = "absolute right-2.5 flex size-4 items-center justify-center";

/** The chosen item's tick: Lucide's check (ISC), drawn inline so a menu loads no icon library. */
function Check() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

export function MenuItem({ className, variant = "default", ...props }: ComponentProps<typeof Primitive.Item> & { variant?: "default" | "destructive" }) {
  return (
    <Primitive.Item
      data-slot="dropdown-menu-item"
      data-variant={variant}
      className={cx(
        ITEM,
        "data-[variant=destructive]:text-destructive data-[variant=destructive]:data-[highlighted]:bg-destructive-tint data-[variant=destructive]:data-[highlighted]:text-destructive",
        className,
      )}
      {...props}
    />
  );
}

export function MenuRadioItem({ className, children, ...props }: ComponentProps<typeof Primitive.RadioItem>) {
  return (
    <Primitive.RadioItem data-slot="dropdown-menu-radio-item" className={cx(ITEM, "pr-8", className)} {...props}>
      {children}
      <span className={INDICATOR}>
        <Primitive.ItemIndicator>
          <Check />
        </Primitive.ItemIndicator>
      </span>
    </Primitive.RadioItem>
  );
}

export function MenuCheckboxItem({ className, children, ...props }: ComponentProps<typeof Primitive.CheckboxItem>) {
  return (
    <Primitive.CheckboxItem data-slot="dropdown-menu-checkbox-item" className={cx(ITEM, "pr-8", className)} {...props}>
      {children}
      <span className={INDICATOR}>
        <Primitive.ItemIndicator>
          <Check />
        </Primitive.ItemIndicator>
      </span>
    </Primitive.CheckboxItem>
  );
}

export function MenuLabel({ className, ...props }: ComponentProps<typeof Primitive.Label>) {
  return (
    <Primitive.Label
      data-slot="dropdown-menu-label"
      className={cx("px-2.5 pb-1.5 pt-2 text-caption font-medium uppercase leading-none tracking-[0.04em] text-muted-foreground", className)}
      {...props}
    />
  );
}

export function MenuSeparator({ className, ...props }: ComponentProps<typeof Primitive.Separator>) {
  return <Primitive.Separator data-slot="dropdown-menu-separator" className={cx("-mx-1 my-1 h-px bg-border", className)} {...props} />;
}
